import { AppDispatch } from "@Reducers/index";
import { getBackendUrl } from "@Utils";
import { getUrlForImage } from "@CubeComponents/constants/globalFunctions";
import axiosWrapper from "../services/axiosWrapper";
import { CONSTANTS } from "@CubeComponents/constants/globalVariable";
import { formCategories } from "@Helpers";
import {
  getPortalUserListAction,
  getPortalUserCountAction,
  setPortalDataAction,
  mergePortalDataAction,
  setPortalEntityListAction,
  setPortalCategoryListAction,
  getCostDatabaseDataAction,
} from "../reducers/portalSetting";
import type {
  GetPortalDataRequest,
  GetPortalDataResponse,
  GetPortalUsedSeatsRequest,
  GetPortalUsedSeatsResponse,
  ChangePortalRolePermissionRequest,
  ChangePortalRolePermissionResponse,
  GetPortalUsersRequest,
  GetPortalUsersResponse,
  AddUserToPortalRequest,
  AddUserToPortalResponse,
  ChangeUserPortalProfileRequest,
  ChangeUserPortalProfileResponse,
  GetPortalEntityListResponse,
  GetFormTemplateRequest,
  GetFormTemplateResponse,
  CustomModuleControllerResponse,
} from "@ServerTypes/portal/portal.schema";
import type {
  DeleteCostDatabaseDataRequest,
  GetCostDatabaseDataRequest,
  GetCostDatabaseDataResponse,
  InsertCostDatabaseDataRequest,
  InsertCostDatabaseDataResponse,
  UpdateCostDatabaseDataRequest,
  UpdateCostDatabaseDataResponse,
} from "@ServerTypes/costDatabase/costDatabase.schema";
import type { UpdateGroupsDataRequest, UpdateGroupsDataResponse } from "@ServerTypes/groups/groups.schema";
import type {
  ProjectTemplateControllerRequest,
  ProjectTemplateControllerResponse,
  SpaceInProjectTemplateRequest,
} from "@ServerTypes/projectTemplates/projectTemplates.schema";
import type { Exact } from "@Helpers/types/exact";
import type { GetResourceStatusCategoryResponse } from "@ServerTypes/resourceStatus/resourceStatus.schema";
import { localDevelopmentOrigin } from "@Helpers/localDevelopment";

// Custom module controller request can have different shapes based on action
interface CustomModuleControllerRequest {
  action?: "insert" | "update" | "delete";
  data?: {
    _id?: string;
    name?: string;
    description?: string;
    singularLabel?: string;
    pluralLabel?: string;
    icon?: string;
  };
  moduleId?: string;
  value?: {
    skip?: number;
    take?: number;
    sorted?: Array<{ name: string; direction: string }>;
    where?: unknown[];
    requiresCounts?: boolean;
  };
}

export function setSettingContent(data: Record<string, unknown>) {
  return {
    type: "SET_SETTING_CONTENT",
    data,
  };
}

export function setStatusData(data: Record<string, unknown>) {
  return {
    type: "SET_STATUS_LIST",
    data,
  };
}

const getPortalUserList = getPortalUserListAction;
const getPortalUserCount = getPortalUserCountAction;
const setPortalData = setPortalDataAction;
const mergePortalData = mergePortalDataAction;
const setPortalEntityList = setPortalEntityListAction;
const setPortalCategoryList = setPortalCategoryListAction;
const getCostDatabaseData = getCostDatabaseDataAction;

// Dispatch to the dedicated per-action endpoints (split from the old customModuleController dispatcher).
const CUSTOM_MODULE_ACTION_PATHS: Record<string, string> = {
  insert: "/portal/insertCustomModule",
  update: "/portal/updateCustomModule",
  delete: "/portal/deleteCustomModule",
};

export async function customModuleController(data: CustomModuleControllerRequest) {
  const path = (data.action && CUSTOM_MODULE_ACTION_PATHS[data.action]) || "/portal/customModuleController";
  const responseData = await axiosWrapper.post<CustomModuleControllerResponse>(`${getBackendUrl()}${path}`, data);
  return responseData;
}

export function getPortalData() {
  return async (dispatch: AppDispatch) => {
    const requestData: GetPortalDataRequest = {
      value: {
        where: [
          {
            isComplex: false,
            field: "frontendUrl",
            operator: "equal",
            value: process.env.REACT_APP_ENV === "development" ? localDevelopmentOrigin : window.location.host,
          },
        ],
      },
    };
    const response = await axiosWrapper.post<GetPortalDataResponse>(
      `${getBackendUrl()}/portal/getPortalData`,
      requestData,
    );
    const portalRow = response.data.result?.[0];
    // `portalDataRow` already types the row (`_id` included); the spread is still taken at the open
    // record type the portal slice is merged as, so only the id read is tightened here.
    const portalData: Record<string, unknown> | undefined = portalRow;
    let data = {
      ...portalData,
      logoUrl: `${getUrlForImage({ id: portalRow?._id }, CONSTANTS.PORTAL)}?v=${Date.now()}`,
      signUpLogoUrl: `${getUrlForImage({ id: portalRow?._id }, CONSTANTS.SIGNUP)}?v=${Date.now()}`,
    };
    // `electronAPI` is injected by the desktop shell's preload script and is absent in the browser,
    // hence the optional calls. Named at the read rather than declared globally.
    (
      window as Window & { electronAPI?: { setPortalDataInDesktop?: (d: unknown) => void } }
    ).electronAPI?.setPortalDataInDesktop?.(data);
    dispatch(setPortalData(data));
  };
}

// KNOWN BUG, server side: `/portal/getPortalUsedSeats` answers a single `countDocuments` number —
// every account in the portal — not the per-role `{ roleName, count }` rows the pro/lite split needs
// (portal.mongo.service.ts:103, and the NOTE on `getPortalUsedSeatsResponse`).
export function getPortalUsedSeats<T extends Exact<GetPortalUsedSeatsRequest, T>>(data: T) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<GetPortalUsedSeatsResponse>(
      `${getBackendUrl()}/portal/getPortalUsedSeats`,
      data,
    );

    const mergedData = {
      usedProSeats: 0,
      usedLiteSeats: 0,
    };

    // The old read asserted that number into an array and threw "resultArray.forEach is not a
    // function", so neither counter ever reached the store. With no role on the count it lands in the
    // same bucket the per-role loop used for every non-`PORTAL_GUEST` role; the lite/guest split
    // stays 0 until the endpoint returns the breakdown.
    if (typeof response.data.result === "number") {
      mergedData.usedProSeats = response.data.result;
    }

    dispatch(mergePortalData(mergedData));
  };
}

// updateProjectRolePermission
export async function changePortalRolePermission<T extends Exact<ChangePortalRolePermissionRequest, T>>(data: T) {
  const res = await axiosWrapper.post<ChangePortalRolePermissionResponse>(
    `${getBackendUrl()}/portal/changePortalRolePermission`,
    data,
  );
  return res;
}

export function getPortalUserDetails<T extends Exact<GetPortalUsersRequest, T>>(data: T) {
  return (dispatch: AppDispatch) => {
    axiosWrapper.post<GetPortalUsersResponse>(`${getBackendUrl()}/portal/getPortalUsers`, data).then((response) => {
      if (response.status) {
        dispatch(getPortalUserList(response.data.result));
        dispatch(getPortalUserCount(response.data.count));
      }
    });
  };
}

// Deliberately dispatches nothing: `addUserToPortalResponse` is `{ status, errorMessage? }` with no
// payload, so there is nothing for a reducer to store.
export function addUserToPortal<T extends Exact<AddUserToPortalRequest, T>>(data: T) {
  return () => axiosWrapper.post<AddUserToPortalResponse>(`${getBackendUrl()}/portal/addUserToPortal`, data);
}

export async function changePortalRole<T extends Exact<ChangeUserPortalProfileRequest, T>>(
  data: T,
): Promise<{ data: ChangeUserPortalProfileResponse }> {
  // this route uniquely wraps its body: res.json({ data })
  const response = await axiosWrapper.post<{ data: ChangeUserPortalProfileResponse }>(
    `${getBackendUrl()}/portal/changeUserPortalProfile`,
    data,
  );
  return response.data;
}

export function getAllPortalEntity() {
  return (dispatch: AppDispatch) => {
    axiosWrapper.get<GetPortalEntityListResponse>(`${getBackendUrl()}/portal/getPortalEntityList`).then((response) => {
      if (response.data.status) {
        dispatch(setPortalEntityList(response.data.data));
      }
    });
  };
}

export function getAllPortalCategories() {
  return (dispatch: AppDispatch) => {
    axiosWrapper
      .get<GetResourceStatusCategoryResponse>(`${getBackendUrl()}/resourceStatus/getResourceStatusCategory`)
      .then((response) => {
        if (response.data.status) {
          dispatch(setPortalCategoryList(response.data.data));
        }
      });
  };
}

export function fetchCostDatabaseData<T extends Exact<GetCostDatabaseDataRequest, T>>(data: T) {
  return async (dispatch: AppDispatch) => {
    axiosWrapper
      .post<GetCostDatabaseDataResponse>(`${getBackendUrl()}/costDatabase/getCostDatabaseData`, data)
      .then((res) => {
        if (res.data.status) {
          dispatch(getCostDatabaseData(res.data.result));
        }
      });
  };
}

// Cost-database CREATE and EDIT now hit the dedicated /insertCostDatabaseData + /updateCostDatabaseData
// routes, split out of the /saveCostDatabaseData dispatcher that branched on `_id` (and had to declare
// `currencyId` as required-on-create/ignored-on-update). `currencyId` is only meaningful on create, so
// only the create sender carries it.
export const insertCostDb = async <T extends Exact<InsertCostDatabaseDataRequest, T>>(
  data: T,
): Promise<{ data: InsertCostDatabaseDataResponse }> => {
  const response = await axiosWrapper.post<InsertCostDatabaseDataResponse>(
    `${getBackendUrl()}/costDatabase/insertCostDatabaseData`,
    data,
  );
  return response;
};

export const updateCostDb = async <T extends Exact<UpdateCostDatabaseDataRequest, T>>(
  data: T,
): Promise<{ data: UpdateCostDatabaseDataResponse }> => {
  const response = await axiosWrapper.post<UpdateCostDatabaseDataResponse>(
    `${getBackendUrl()}/costDatabase/updateCostDatabaseData`,
    data,
  );
  return response;
};

// Cost-database DELETE now hits the dedicated /deleteCostDatabaseData route (split out of the read-only
// /getCostDatabaseData). Response shape is a status envelope (status + errorMessage), a subset of GetCostDatabaseDataResponse.
export const deleteCostDb = async (ids: string | string[]): Promise<{ data: GetCostDatabaseDataResponse }> => {
  const data: DeleteCostDatabaseDataRequest = {
    key: ids,
  };
  const response = await axiosWrapper.post<GetCostDatabaseDataResponse>(
    `${getBackendUrl()}/costDatabase/deleteCostDatabaseData`,
    data,
  );
  return response;
};

/** The group write operations, each of which is its own server route. */
export type GroupsWriteAction =
  "insert" | "update" | "remove" | "delete" | "updateRoleOfUsers" | "setRoleOfUsers" | "remove_user";

// Dispatch to the dedicated per-action group endpoints (split from the old updateGroupsData dispatcher).
const GROUPS_ACTION_PATHS: Record<GroupsWriteAction, string> = {
  insert: "/groups/insertGroups",
  update: "/groups/updateGroups",
  remove: "/groups/removeGroups",
  delete: "/groups/deleteGroups",
  updateRoleOfUsers: "/groups/updateRoleOfUsers",
  setRoleOfUsers: "/groups/setRoleOfUsers",
  remove_user: "/groups/removeUsersFromGroups",
};

/**
 * The operation is an ARGUMENT, not a body field: the server dispatches by route path, so a
 * discriminant in the body would be dead weight (and a way for a caller to steer which branch runs).
 * Omit `action` to hit the read/list route.
 */
export const updateGroupsData = async <T extends Exact<UpdateGroupsDataRequest, T>>(
  data: T,
  action?: GroupsWriteAction,
): Promise<{ data: UpdateGroupsDataResponse }> => {
  const path = action ? GROUPS_ACTION_PATHS[action] : "/groups/updateGroupsData";
  const response = await axiosWrapper.post<UpdateGroupsDataResponse>(`${getBackendUrl()}${path}`, data);
  return response;
};

export const getProjectFormTemplate = async (): Promise<{ data: GetFormTemplateResponse }> => {
  const data = {
    resourceType: formCategories.projects,
  } satisfies GetFormTemplateRequest;
  const response = await axiosWrapper.post<GetFormTemplateResponse>(`${getBackendUrl()}/portal/getFormTemplate`, data);
  return response;
};

// Each former action-dispatcher is split into dedicated endpoints. These maps route an `action`
// to its endpoint path; an unmapped/absent action falls back to the list endpoint.
const PROJECT_TEMPLATE_ACTION_PATHS: Record<string, string> = {
  insert: "/projectTemplates/saveProjectTemplateData",
  edit: "/projectTemplates/saveProjectTemplateData",
  delete: "/projectTemplates/deleteProjectTemplate",
};
const SPACE_IN_TEMPLATE_ACTION_PATHS: Record<string, string> = {
  insert: "/projectTemplates/insertSpaceInProjectTemplate",
  edit: "/projectTemplates/editSpaceInProjectTemplate",
  delete: "/projectTemplates/deleteSpaceInProjectTemplate",
};

export const projectTemplateData = async <T extends Exact<ProjectTemplateControllerRequest & { action?: string }, T>>(
  data: T,
): Promise<{ data: ProjectTemplateControllerResponse }> => {
  const action = (data as { action?: string }).action;
  const path = (action && PROJECT_TEMPLATE_ACTION_PATHS[action]) || "/projectTemplates/projectTemplateData";
  const response = await axiosWrapper.post<ProjectTemplateControllerResponse>(`${getBackendUrl()}${path}`, data);
  return response;
};

export const spaceDataInProjectTemplate = async <
  T extends Exact<SpaceInProjectTemplateRequest & { action?: string }, T>,
>(
  data: T,
): Promise<{ data: ProjectTemplateControllerResponse }> => {
  const action = (data as { action?: string }).action;
  const path = (action && SPACE_IN_TEMPLATE_ACTION_PATHS[action]) || "/projectTemplates/spacesDataInProjectTemplate";
  const response = await axiosWrapper.post<ProjectTemplateControllerResponse>(`${getBackendUrl()}${path}`, data);
  return response;
};
